import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'hooks';
import { loadDictionary } from 'api/dictionaries';
import { RootState } from 'store';
import userApi from 'api/user';
import Panel from 'components/ui/panel';
import { Skeleton } from 'components/ui/skeleton';
import { ThemeSwitchCard } from '../ThemeSwitcherCard';
import LocalStorage from 'helpers/LocalStorage';
import { setAppTheme } from 'theme';
import { updateBrandConfig } from 'actions/settings';
import { debounce } from 'lodash-es';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ThemeSwitcherPanel: FC<Props> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const isFirstRender = useRef(true);
  const getTranslate = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const { brandTheme, selectedThemeId } = useSelector((state: RootState) => {
    return {
      brandTheme: state.filtersValues.brandTheme,
      selectedThemeId: state.settings.brandConfig.id,
    };
  });

  useEffect(() => {
    if (isOpen && isFirstRender.current) {
      fetchBrandTheme();
    }
  }, [isOpen]);

  const fetchBrandTheme = async () => {
    try {
      await loadDictionary({
        name: 'brandTheme',
      });
      isFirstRender.current = false;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomTheme = debounce(async (selectedThemeId: string) => {
    try {
      const { customTheme } = await userApi.setConfig({
        param: 'customTheme',
        value: selectedThemeId,
      });

      setAppTheme(customTheme.config.theme);
      dispatch(updateBrandConfig(customTheme));
      LocalStorage.set('brandConfig', customTheme);
    } catch (err) {
      console.error(err);
    }
  }, 300);

  return (
    <Panel
      modern
      isOpened={isOpen}
      customClass='ui-panel_fixed ui-panel_theme-switcher'
      excludeFromCloseTrigger={['.theme-switcher-button']}
      onClose={onClose}
      title={getTranslate('theme.switcher.title')}>
      <div className='theme-switcher'>
        <div className='theme-switcher__subtitle'>
          {getTranslate('theme.switcher.subtitle')}
        </div>
        <div className='theme-switcher__content'>
          {isLoading
            ? [1, 2, 3].map((item) => (
                <div key={item}>
                  <Skeleton
                    width={114}
                    height={64}
                    style={{ marginBottom: 12 }}
                  />
                  <Skeleton width={80} height={16} />
                </div>
              ))
            : brandTheme?.list.map((item) => (
                <ThemeSwitchCard
                  {...item}
                  key={item.id}
                  selectedId={selectedThemeId}
                  onSelect={(value) => {
                    handleSaveCustomTheme(value);
                  }}
                />
              ))}
        </div>
      </div>
    </Panel>
  );
};
